import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeftRight, BadgeCheck, ChevronDown, LogOut, Monitor, Moon, ShieldCheck, Settings2, Sun, UserRound } from 'lucide-react';
import { useToast, Avatar, Menu, MenuContent, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger } from '@/components/ui';
import { isPlatformOperator, useSession } from '@/lib/session';
import { useTheme } from '@/lib/theme';
import { useDirtyGuard } from '@/features/platform/ui/dirtyGuard';
import type { ThemePreference } from '@/lib/types';


export const TWO_FACTOR_TICK_LABEL = 'Two-factor authentication enabled and verified';

/**
 * Blue verified tick (#1a73e8). Render it only from the session's `user.twoFactorEnabled`, which the
 * API sets from `user.twoFactor.enabled` — true only after an authenticator code was verified during
 * enrolment. It is unrelated to email verification.
 */
export function TwoFactorTick({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <span className={className ? `verified-tick ${className}` : 'verified-tick'} style={{ width: size, height: size }} title={TWO_FACTOR_TICK_LABEL} role="img" aria-label={TWO_FACTOR_TICK_LABEL}>
      <BadgeCheck size={Math.max(8, size - 4)} aria-hidden />
    </span>
  );
}

export function UserMenu({ variant }: { variant: 'chip' | 'avatar' }) {
  const { user, principal, contexts, signOut, can } = useSession();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const { isDirty, guardedNavigate } = useDirtyGuard();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  if (!user) return null;

  const context = principal?.kind === 'client' ? principal.clientName : principal?.tenantName;
  const twoFactor = user.twoFactorEnabled === true;

  return (
    <>
    <Menu>
      <MenuTrigger asChild>
        {variant === 'chip' ? (
          <button type="button" className="user-chip" aria-label={`Account menu for ${user.name}${twoFactor ? `, ${TWO_FACTOR_TICK_LABEL.toLowerCase()}` : ''}`}>
            <Avatar src={user.avatarUrl} name={user.name} size={28} />
            <span className="user-chip-text">
              <strong className="user-chip-name">
                {user.name}
                {twoFactor && <TwoFactorTick />}
              </strong>
              <span>{user.email}</span>
            </span>
            <ChevronDown className="user-chip-chevron" size={16} aria-hidden />
          </button>
        ) : (
          <button type="button" className="btn btn-ghost btn-icon-only btn-md" aria-label={`Account menu for ${user.name}${twoFactor ? `, ${TWO_FACTOR_TICK_LABEL.toLowerCase()}` : ''}`} title={twoFactor ? `${user.name} · ${TWO_FACTOR_TICK_LABEL}` : user.name}>
            <span style={{ position: 'relative', display: 'inline-grid' }}>
              <Avatar src={user.avatarUrl} name={user.name} size={28} />
              {twoFactor && (
                <span style={{ position: 'absolute', right: -4, bottom: -3, display: 'inline-flex' }}>
                  <TwoFactorTick size={14} />
                </span>
              )}
            </span>
          </button>
        )}
      </MenuTrigger>
      <MenuContent align={variant === 'chip' ? 'start' : 'end'} minWidth={240}>
        <div className="menu-user">
          <strong className="user-chip-name">
            {user.name}
            {twoFactor && <TwoFactorTick />}
          </strong>
          <span>{user.email}</span>
          <span className={twoFactor ? 'menu-user-2fa is-on' : 'menu-user-2fa'}>{twoFactor ? TWO_FACTOR_TICK_LABEL : 'Two-factor authentication off'}</span>
          {principal && (
            <span className="menu-user-ctx">
              {principal.roleLabel}
              {context ? ` · ${context}` : ''}
            </span>
          )}
        </div>
        <MenuSeparator />
        <MenuLabel>Theme</MenuLabel>
        <MenuRadioGroup value={preference} onValueChange={(v) => setPreference(v as ThemePreference)}>
          <MenuRadioItem value="light" icon={<Sun />}>Light</MenuRadioItem>
          <MenuRadioItem value="dark" icon={<Moon />}>Dark</MenuRadioItem>
          <MenuRadioItem value="system" icon={<Monitor />}>System</MenuRadioItem>
        </MenuRadioGroup>
        <MenuSeparator />
        {principal?.kind === 'client' && <><MenuItem icon={<UserRound />} onSelect={() => navigate('/portal/profile')}>Profile</MenuItem><MenuItem icon={<UserRound />} onSelect={() => navigate('/portal/account')}>Account</MenuItem></>}
        {principal?.kind === 'staff' && !isPlatformOperator(user) && (can('tenant.settings.manage') || can('staff.manage') || can('roles.manage') || can('tenant.billing.manage')) && <MenuItem icon={<Settings2 />} onSelect={() => navigate('/settings')}>Workspace settings</MenuItem>}
        {isPlatformOperator(user) && <MenuItem icon={<UserRound />} onSelect={() => guardedNavigate('/platform/account')}>Account</MenuItem>}
        {isPlatformOperator(user) && <MenuItem icon={<Settings2 />} onSelect={() => isDirty() ? guardedNavigate('/platform/settings') : navigate('/platform/settings')}>Platform settings</MenuItem>}
        {principal?.kind === 'staff' && !isPlatformOperator(user) && (
          <MenuItem icon={<UserRound />} onSelect={() => navigate('/account')}>
            Account
          </MenuItem>
        )}
        {isPlatformOperator(user) && (
          <MenuItem icon={<ShieldCheck />} onSelect={() => navigate('/platform')}>
            Platform administration
          </MenuItem>
        )}
        {(contexts.length > 1 || (isPlatformOperator(user) && contexts.length > 0 && !principal)) && (
          <MenuItem icon={<ArrowLeftRight />} onSelect={() => navigate('/workspaces')} description={`${contexts.length} workspace${contexts.length === 1 ? '' : 's'} available`}>
            {principal ? 'Switch workspace' : 'Open a workspace'}
          </MenuItem>
        )}
        <MenuItem
          icon={<LogOut />}
          disabled={signingOut}
          onSelect={() => {
            setSigningOut(true);
            void signOut().then(() => {
              // A document navigation also discards mounted route state and pending mutations.
              window.location.replace('/login');
            }).catch((error: unknown) => {
              setSigningOut(false);
              toast.show({ tone: 'danger', title: 'Could not sign out', description: error instanceof Error ? error.message : 'Please try again.' });
            });
          }}
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </MenuItem>
      </MenuContent>
    </Menu>

    </>
  );
}
